// src/services/chatService.js
import {
  CHAT_API_URL,
  CHAT_DOCUMENT_API_URL,
  CLEAR_CHAT_API_URL,
  SAVE_ESTIMATION_API_URL,
  DASHBOARD_ESTIMATIONS_API_URL,
  CHAT_HISTORY_API_URL,
  REQUEST_TIMEOUT_MS,
} from "../core/config";
import { postJson, getJson, deleteJson, mergeAbortSignals } from "./apiClient";
import { authHeaders } from "./authService";
import { prepareDocumentUploadFile } from "../utils/documentUpload";

function mapChatApiResponse(data, sessionId) {
  const aiReply = data.ai_reply ?? data.reply ?? data.message ?? data.answer ?? null;
  if (!aiReply) {
    throw new Error("Backend did not return an ai_reply field.");
  }
  return {
    sessionId: data.session_id ?? sessionId,
    aiReply,
    slot: data.slot ?? null,
    options: data.options ?? null,
    preselectedOptions: Array.isArray(data.preselected_options) ? data.preselected_options : null,
    selection_type: data.selection_type ?? null,
    projectScope: data.project_scope ?? null,
    estimationValue: data.estimation_value ?? null,
    featureOptions: data.feature_options ?? null,
    estimationOptions: Array.isArray(data.estimation_options) ? data.estimation_options : null,
    qaHours: data.qa_hours ?? null,
    moduleEstimatedHours: data.module_estimated_hours ?? null,
    estimationSummary: data.estimation_summary ?? null,
    totalHoursDict: data.total_hours_dict ?? null,
    deliveryLayerTotals: data.delivery_layer_totals ?? null,
    totalHours: data.total_hours ?? null,
    phases: Array.isArray(data.phases) ? data.phases : null,
  };
}

/**
 * Simple non-streaming chat call.
 *
 * Backend endpoint:
 *   POST CHAT_API_URL
 *   Body: { session_id: string, user_input: string, slot?: string, updated_project_scope?: object, document_text?: string }
 *   Response JSON: { ai_reply: string, slot?: string, options?: string[], preselected_options?: string[], project_scope?: object, estimation_value?, qa_hours? }
 */
export async function sendChatMessage(
  sessionId,
  userInput,
  slot = null,
  updatedProjectScope = null,
  documentText = null,
  extras = null
) {
  // Validate inputs - backend expects non-null strings
  if (!sessionId || typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new Error("sessionId must be a non-empty string");
  }
  if (!userInput || typeof userInput !== 'string' || userInput.trim() === '') {
    throw new Error("userInput must be a non-empty string");
  }

  // Build payload - backend ChatParamModel allows None but code expects strings
  // Always send strings, never null/undefined to avoid backend AttributeError
  // IMPORTANT: Preserve exact case for "Confirm" and "Cancel" - backend does case-sensitive comparison
  const payload = {
    session_id: String(sessionId).trim(),
    user_input: String(userInput), // Preserve exact case - backend needs "Confirm" and "Cancel" with capital letters
  };

  // Only include slot if it has a value (backend Optional[str] = None)
  if (slot !== null && slot !== undefined && slot !== "") {
    payload.slot = String(slot).trim();
  }

  // Include updated_project_scope when confirming scope (role -> module -> layer -> string[])
  if (updatedProjectScope != null && typeof updatedProjectScope === 'object' && Object.keys(updatedProjectScope).length > 0) {
    payload.updated_project_scope = updatedProjectScope;
  }

  const doc = documentText != null ? String(documentText).trim() : '';
  if (doc) {
    payload.document_text = doc;
  }
  
  // Double-check we're not sending null/undefined values
  if (payload.session_id === '' || payload.user_input === '') {
    throw new Error("session_id and user_input cannot be empty strings");
  }
  
  // Verify payload types before sending
  if (payload.session_id === null || payload.session_id === undefined) {
    throw new Error("session_id cannot be null or undefined");
  }
  if (payload.user_input === null || payload.user_input === undefined) {
    throw new Error("user_input cannot be null or undefined");
  }

  // Use config timeout for all chat requests (e.g. 2 hours for long-running estimation)
  const timeout = REQUEST_TIMEOUT_MS;
  const signal = extras != null && typeof extras === "object" ? extras.signal : undefined;

  try {
    const data = await postJson(CHAT_API_URL, payload, { timeout, signal });

    return mapChatApiResponse(data, sessionId);
  } catch (error) {
    if (error.name === "AbortError") {
      throw error;
    }
    if (error.message.includes("timeout") || error.message.includes("too long")) {
      throw new Error("The request is taking longer than expected. This might be due to generating a detailed module & feature wise estimation. Please wait a moment and try again.");
    }

    throw error;
  }
}

/**
 * BRD / document upload via multipart (server extracts text — same pipeline as document_text on POST /chat).
 * Backend: POST CHAT_DOCUMENT_API_URL — Form: session_id, user_input (optional), slot (optional), file.
 */
export async function sendChatDocument(sessionId, file, { userInput = "", slot = null, signal: externalSignal } = {}) {
  if (!sessionId || typeof sessionId !== "string" || sessionId.trim() === "") {
    throw new Error("sessionId must be a non-empty string");
  }
  const uploadFile = prepareDocumentUploadFile(file);

  const formData = new FormData();
  formData.append("session_id", String(sessionId).trim());
  formData.append("user_input", userInput != null ? String(userInput) : "");
  if (slot != null && slot !== undefined && slot !== "") {
    formData.append("slot", String(slot).trim());
  }
  formData.append("file", uploadFile, uploadFile.name || "upload");

  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);
  const mergedSignal = mergeAbortSignals(externalSignal, timeoutController.signal);

  try {
    const response = await fetch(CHAT_DOCUMENT_API_URL, {
      method: "POST",
      headers: authHeaders(),
      body: formData,
      signal: mergedSignal,
    });

    if (!response.ok) {
      if (response.status === 413) {
        throw new Error(
          "The upload is too large for the server (413). Try a smaller file, or ensure nginx is configured with client_max_body_size (e.g. 100m) and rebuild the UI container."
        );
      }
      let errorText = "";
      try {
        const errorData = await response.json();
        errorText = errorData.detail || errorData.message || errorData.error || JSON.stringify(errorData);
      } catch {
        errorText = await response.text().catch(() => response.statusText);
      }
      throw new Error(`API error (${response.status}): ${errorText || response.statusText}`);
    }

    const data = await response.json();
    return mapChatApiResponse(data, sessionId);
  } catch (error) {
    if (error.name === "AbortError") {
      throw error;
    }
    if (String(error.message || "").includes("aborted")) {
      throw new Error("Request timed out. The server is taking too long to respond. Please try again.");
    }
    if (error.message.includes("Failed to fetch") || error.message.includes("NetworkError")) {
      throw new Error("Network error: Unable to connect to the server. Please check your connection and try again.");
    }
    if (error.message.includes("timeout") || error.message.includes("too long")) {
      throw new Error(
        "The request is taking longer than expected. This might be due to generating a detailed module & feature wise estimation. Please wait a moment and try again."
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Start a fresh chat: backend removes the previous session from Redis and returns new_session_id.
 *
 * Backend endpoint: POST /clear_chat
 * Body: { session_id: string, user_input: string }
 * Response: { new_session_id: string, reply?, success?, message? }
 */
export async function clearChatSession(sessionId) {
  const sid = sessionId != null ? String(sessionId).trim() : "";
  if (!sid) {
    throw new Error("clearChatSession requires a non-empty session_id");
  }
  try {
    return await postJson(CLEAR_CHAT_API_URL, {
      session_id: sid,
      user_input: ""
    });
  } catch (error) {
    console.error("Error getting new session from clear_chat:", error);
    throw error;
  }
}

/**
 * Save full estimation to backend DB.
 *
 * Backend endpoint: POST /save_estimation_items
 * Body: { session_id, estimation_id?, owner_id?, chat_snapshot? }
 * Response: { success, data: { estimation_id, inserted_rows, has_chat_snapshot, ... } }
 */
export async function saveEstimationItems(sessionId, options = null) {
  const sid = sessionId != null ? String(sessionId).trim() : "";
  if (!sid) {
    throw new Error("saveEstimationItems requires a non-empty session_id");
  }

  let estimationId = null;
  let ownerId = null;
  let chatSnapshot = null;
  let estimationValue = null;
  let projectName = null;
  let platform = null;

  if (options != null && typeof options === "object" && !Number.isFinite(Number(options))) {
    estimationId = options.estimationId ?? options.estimation_id ?? null;
    ownerId = options.ownerId ?? options.owner_id ?? null;
    chatSnapshot = options.chatSnapshot ?? options.chat_snapshot ?? null;
    estimationValue = options.estimationValue ?? options.estimation_value ?? null;
    projectName = options.projectName ?? options.project_name ?? null;
    platform = options.platform ?? null;
  } else if (options != null && Number.isFinite(Number(options))) {
    estimationId = Number(options);
  }

  const payload = { session_id: sid };
  if (estimationId != null && Number.isFinite(Number(estimationId))) {
    payload.estimation_id = Number(estimationId);
  }
  if (ownerId != null && String(ownerId).trim()) {
    payload.owner_id = String(ownerId).trim();
  }
  if (estimationValue != null && typeof estimationValue === "object") {
    payload.estimation_value = estimationValue;
  }
  if (projectName != null && String(projectName).trim()) {
    payload.project_name = String(projectName).trim();
  }
  if (platform != null && String(platform).trim()) {
    payload.platform = String(platform).trim();
  }
  if (chatSnapshot != null && typeof chatSnapshot === "object") {
    payload.chat_snapshot = chatSnapshot;
  }

  return postJson(SAVE_ESTIMATION_API_URL, payload, { timeout: REQUEST_TIMEOUT_MS });
}

/**
 * List saved estimation snapshots for dashboard (owner-scoped).
 * GET /dashboard/estimations?owner_id=&limit=
 */
export async function listDashboardEstimations(ownerId, limit = 25) {
  const oid = ownerId != null ? String(ownerId).trim() : "";
  if (!oid) {
    throw new Error("listDashboardEstimations requires owner_id");
  }
  const safeLimit = Math.min(100, Math.max(1, Number(limit) || 25));
  const qs = new URLSearchParams({ owner_id: oid, limit: String(safeLimit) });
  return getJson(`${DASHBOARD_ESTIMATIONS_API_URL}?${qs.toString()}`, { timeout: 60000 });
}

/**
 * Load one saved estimation (session_state + chat_snapshot).
 * GET /dashboard/estimations/{id}?owner_id=
 */
export async function getDashboardEstimation(estimationId, ownerId = null) {
  const eid = Number(estimationId);
  if (!Number.isFinite(eid)) {
    throw new Error("getDashboardEstimation requires a valid estimation_id");
  }
  const path = `${DASHBOARD_ESTIMATIONS_API_URL}/${eid}`;
  const oid = ownerId != null ? String(ownerId).trim() : "";
  const url = oid ? `${path}?${new URLSearchParams({ owner_id: oid }).toString()}` : path;
  return getJson(url, { timeout: 60000 });
}

/**
 * List chat sessions for the authenticated user.
 * Backend keeps active sessions in Redis, flushes to Postgres on a schedule, and
 * returns a unified list with summary fields per session.
 *
 * GET /chat/history
 * Response: { success, data: [{ session_id, tag, title, platform, created_at, message_count, source }] }
 *   source is "redis" (live) or "postgres" (flushed from Redis).
 */
export async function listChatHistories() {
  return getJson(CHAT_HISTORY_API_URL, { timeout: 60000 });
}

/**
 * Load one chat session (messages + optional slot / chat_snapshot).
 * GET /chat/history/{session_id}
 */
export async function getChatHistory(sessionId) {
  const sid = sessionId != null ? String(sessionId).trim() : "";
  if (!sid) {
    throw new Error("getChatHistory requires session_id");
  }
  return getJson(`${CHAT_HISTORY_API_URL}/${encodeURIComponent(sid)}`, { timeout: 60000 });
}

/**
 * Remove one chat history session.
 * DELETE /chat/history/{session_id}
 */
export async function deleteChatHistory(sessionId) {
  const sid = sessionId != null ? String(sessionId).trim() : "";
  if (!sid) {
    throw new Error("deleteChatHistory requires session_id");
  }
  return deleteJson(`${CHAT_HISTORY_API_URL}/${encodeURIComponent(sid)}`, { timeout: 60000 });
}

/**
 * Remove a saved estimation snapshot (server must expose DELETE on the same path).
 * DELETE /dashboard/estimations/{id}?owner_id=
 */

export async function deleteDashboardEstimation(estimationId, ownerId = null) {
  const eid = Number(estimationId);
  if (!Number.isFinite(eid)) {
    throw new Error("deleteDashboardEstimation requires a valid estimation_id");
  }
  const path = `${DASHBOARD_ESTIMATIONS_API_URL}/${eid}`;
  const oid = ownerId != null ? String(ownerId).trim() : "";
  const url = oid ? `${path}?${new URLSearchParams({ owner_id: oid }).toString()}` : path;
  return deleteJson(url, { timeout: 60000 });
}
